import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';

// Cookie categories and their descriptions
const COOKIE_CATEGORIES = {
  necessary: {
    name: 'Necessary',
    description: 'Essential cookies required for the website to function. These cannot be disabled.',
    required: true,
    cookies: ['session', 'auth_token', 'csrf_token']
  },
  functional: {
    name: 'Functional',
    description: 'Cookies that enable enhanced functionality like remembering your preferences and recent views.',
    required: false,
    cookies: ['recently_viewed', 'theme_preference', 'language']
  },
  analytics: {
    name: 'Analytics',
    description: 'Cookies that help us understand how visitors interact with the website to improve user experience.',
    required: false,
    cookies: ['_ga', '_gid', 'analytics_session']
  },
  marketing: {
    name: 'Marketing',
    description: 'Cookies used to deliver relevant advertisements and track ad campaign performance.',
    required: false,
    cookies: ['_fbp', 'ads_tracking']
  }
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie_consent');
    if (!consent) {
      // Wait a moment before showing to not be intrusive
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
    // Note: preferences are initialized from state, no need to update from localStorage here
    // as the default matches what we want for returning users
  }, []);

  const savePreferences = (prefs) => {
    localStorage.setItem('cookie_consent', JSON.stringify(prefs));
    localStorage.setItem('cookie_consent_date', new Date().toISOString());
    setPreferences(prefs);
    setIsVisible(false);
    setShowPreferences(false);

    // Apply preferences (clear non-consented cookies)
    if (!prefs.analytics) {
      // Clear analytics cookies
      document.cookie = '_ga=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = '_gid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
    if (!prefs.marketing) {
      // Clear marketing cookies
      document.cookie = '_fbp=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    }
  };

  const acceptAll = () => {
    savePreferences({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptNecessaryOnly = () => {
    savePreferences({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    });
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Cookie Banner — slim non-intrusive bottom bar */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[9999] animate-in slide-in-from-bottom duration-300"
        role="dialog"
        aria-label="Cookie consent"
        data-testid="cookie-consent-banner"
      >
        <div className="bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-lg">
          <div className="container mx-auto px-4 py-3 md:px-6 lg:px-8">
            {!showPreferences ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Cookie className="h-4 w-4 text-[#0F2C3E] flex-shrink-0" />
                  <p className="text-sm text-slate-600 truncate sm:truncate-none">
                    We use cookies to improve your experience.{' '}
                    <Link to="/cookies" className="text-[#0F2C3E] hover:underline font-medium">Learn more</Link>
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPreferences(true)}
                    className="text-xs h-8 px-3"
                    data-testid="cookie-customize-btn"
                  >
                    <Settings className="h-3.5 w-3.5 mr-1" />
                    Customise
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={acceptNecessaryOnly}
                    className="text-xs h-8 px-3"
                    data-testid="cookie-reject-btn"
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={acceptAll}
                    className="bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 text-xs h-8 px-4"
                    data-testid="cookie-accept-btn"
                  >
                    Accept All
                  </Button>
                </div>
              </div>
            ) : (
              // Preferences panel
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0F2C3E]/10 flex items-center justify-center">
                      <Settings className="h-5 w-5 text-[#0F2C3E]" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Cookie Preferences</h3>
                  </div>
                  <button 
                    onClick={() => setShowPreferences(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                    aria-label="Close preferences"
                  >
                    <X className="h-5 w-5 text-slate-500" />
                  </button>
                </div>
                
                <div className="grid gap-4 mb-6 max-h-[50vh] overflow-y-auto">
                  {Object.entries(COOKIE_CATEGORIES).map(([key, category]) => (
                    <div 
                      key={key}
                      className="flex items-start justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                    >
                      <div className="flex-1 pr-4">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-slate-900">{category.name}</h4>
                          {category.required && (
                            <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">Required</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">{category.description}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Cookies: {category.cookies.join(', ')}
                        </p>
                      </div>
                      <Switch
                        checked={preferences[key]}
                        onCheckedChange={(checked) => {
                          if (!category.required) {
                            setPreferences(prev => ({ ...prev, [key]: checked }));
                          }
                        }}
                        disabled={category.required}
                        className={category.required ? 'opacity-50 cursor-not-allowed' : ''}
                        data-testid={`cookie-switch-${key}`}
                      />
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowPreferences(false)}
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={saveCustomPreferences}
                    className="flex-1 bg-[#0F2C3E] hover:bg-[#0F2C3E]/90 gap-2"
                    data-testid="cookie-save-preferences-btn"
                  >
                    <Check className="h-4 w-4" />
                    Save Preferences
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// Export utility to check consent status
export const getCookieConsent = () => {
  try {
    const consent = localStorage.getItem('cookie_consent');
    if (consent) {
      return JSON.parse(consent);
    }
  } catch (e) {
    console.error('Failed to get cookie consent:', e);
  }
  return null;
};

// Export utility to reset consent (for cookie settings page)
export const resetCookieConsent = () => {
  localStorage.removeItem('cookie_consent');
  localStorage.removeItem('cookie_consent_date');
  window.location.reload();
};

export default CookieConsent;
